/**
 * Data layer for `bulk-campaign-actions`. Thin typed fetch wrappers over
 * `/api/v1/bulk-campaign-actions` (FS-814). Raw `fetch()` is confined to
 * this file per the dash contract (D6).
 *
 * Endpoints (tenant-scoped on the API side):
 *   POST /               { campaignIds[], action } -> RunBulkActionResponse
 *   GET  /jobs           list recent jobs (newest first)
 *   GET  /job/:jobId     fetch a single job
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const BULK_ACTIONS = [
	"pause",
	"activate",
	"archive",
	"delete",
] as const;
export type BulkAction = (typeof BULK_ACTIONS)[number];

export const BULK_JOB_STATUSES = [
	"queued",
	"running",
	"succeeded",
	"failed",
] as const;
export type BulkJobStatus = (typeof BULK_JOB_STATUSES)[number];

export const MAX_CAMPAIGN_IDS = 200;

export const BULK_ACTION_LABELS: Record<BulkAction, string> = {
	pause: "Pause",
	activate: "Activate",
	archive: "Archive",
	delete: "Delete",
};

export const BULK_ACTION_VERBS: Record<BulkAction, string> = {
	pause: "paused",
	activate: "activated",
	archive: "archived",
	delete: "deleted",
};

export const DESTRUCTIVE_ACTIONS: ReadonlySet<BulkAction> = new Set<BulkAction>([
	"delete",
	"archive",
]);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RejectedEntry {
	campaign_id: string;
	error: string;
}

export interface ResultEntry {
	campaign_id: string;
	ok: boolean;
	error?: string;
}

export interface RunBulkActionBody {
	campaignIds: string[];
	action: BulkAction;
}

export interface RunBulkActionResponse {
	jobId: string;
	queued: number;
	accepted: number;
	rejected: RejectedEntry[];
	status: BulkJobStatus;
}

export interface BulkJobView {
	id: string;
	action: BulkAction;
	status: BulkJobStatus;
	campaign_ids: string[];
	results: ResultEntry[];
	accepted: number;
	rejected: number;
	queued: number;
	created_at: number;
	updated_at: number;
}

export interface ListJobsResponse {
	jobs: BulkJobView[];
	count: number;
}

export interface CampaignOption {
	id: string;
	name: string;
}

export interface CampaignListResponse {
	campaigns: CampaignOption[];
}

// ---------------------------------------------------------------------------
// Validation (pure, reusable in tests)
// ---------------------------------------------------------------------------

export type ValidateResult =
	| { ok: true; body: RunBulkActionBody }
	| { ok: false; errors: { campaignIds?: string; action?: string } };

export function isValidBulkAction(v: unknown): v is BulkAction {
	return (
		typeof v === "string" &&
		(BULK_ACTIONS as readonly string[]).includes(v)
	);
}

export function validateRunBody(input: {
	campaignIds: string[];
	action: string | null;
}): ValidateResult {
	const errors: { campaignIds?: string; action?: string } = {};

	if (!input.action || !isValidBulkAction(input.action)) {
		errors.action = "choose an action";
	}

	const ids = Array.from(
		new Set(input.campaignIds.map((s) => s.trim()).filter(Boolean)),
	);

	if (ids.length === 0) {
		errors.campaignIds = "select at least one campaign";
	} else if (ids.length > MAX_CAMPAIGN_IDS) {
		errors.campaignIds = `max ${MAX_CAMPAIGN_IDS} per batch`;
	}

	if (errors.campaignIds || errors.action) {
		return { ok: false, errors };
	}

	return {
		ok: true,
		body: { campaignIds: ids, action: input.action as BulkAction },
	};
}

// ---------------------------------------------------------------------------
// Derived helpers
// ---------------------------------------------------------------------------

export function statusBadgeClass(status: BulkJobStatus): string {
	switch (status) {
		case "succeeded":
			return "bg-emerald-50 text-emerald-700 border-emerald-200";
		case "running":
			return "bg-sky-50 text-sky-700 border-sky-200";
		case "queued":
			return "bg-zinc-50 text-zinc-700 border-zinc-200";
		case "failed":
			return "bg-red-50 text-red-700 border-red-200";
		default:
			return "bg-zinc-50 text-zinc-700 border-zinc-200";
	}
}

export function summarizeResponse(r: RunBulkActionResponse): string {
	const parts: string[] = [];
	parts.push(`${r.accepted} accepted`);
	if (r.rejected.length > 0) parts.push(`${r.rejected.length} rejected`);
	parts.push(`queued ${r.queued}`);
	return parts.join(" - ");
}

// ---------------------------------------------------------------------------
// Fetch wrappers — raw fetch only lives in this file (D6)
// ---------------------------------------------------------------------------

const BASE = "/api/v1/bulk-campaign-actions";
const CAMPAIGNS_BASE = "/api/v1/campaigns";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function runBulkAction(
	body: RunBulkActionBody,
): Promise<RunBulkActionResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<RunBulkActionResponse>(res);
}

export async function listJobs(): Promise<ListJobsResponse> {
	const res = await fetch(`${BASE}/jobs`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListJobsResponse>(res);
}

export async function getJob(jobId: string): Promise<{ job: BulkJobView }> {
	const res = await fetch(
		`${BASE}/job/${encodeURIComponent(jobId)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<{ job: BulkJobView }>(res);
}

export async function listCampaigns(): Promise<CampaignListResponse> {
	const res = await fetch(CAMPAIGNS_BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<CampaignListResponse>(res);
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const bulkCampaignActionsQueryKeys = {
	all: ["bulk-campaign-actions"] as const,
	jobs: () => [...bulkCampaignActionsQueryKeys.all, "jobs"] as const,
	job: (id: string) =>
		[...bulkCampaignActionsQueryKeys.all, "job", id] as const,
	campaigns: () =>
		[...bulkCampaignActionsQueryKeys.all, "campaigns"] as const,
};
